import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  App,
  Button,
  theme,
  Upload,
  Flex,
  Space,
  Popover,
  Modal,
  AutoComplete,
  notification as antdNotification,
} from 'antd'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import * as XLSX from 'xlsx'
import {
  DownloadOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import demoxlsadditivematerials from '$assets/demoxlsadditivematerials.xlsx'
import Tooltip from '$components/Tooltip'
import Spinner from '$components/Spinner'
import {
  createOperatorAdditiveMaterial,
  createOperatorAdditiveMaterials,
  fetchAllOperatorAdditiveMaterials,
  fetchAutocompleteOperatorAdditiveMaterials,
} from '$api/evoAPIs'

const normalizeName = (name) => {
  if (!name || typeof name !== 'string') return ''
  return name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
}

const { useToken } = theme

const AddOperatorAdditiveMaterial = ({
  setHighlightedMaterialId,
  setListActionTrigger,
}) => {
  const { t } = useTranslation()
  const { token } = useToken()

  const { notification } = App.useApp()

  const [loading, setLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [items, setItems] = useState([])
  const [fileList, setFileList] = useState([])

  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [newMaterialName, setNewMaterialName] = useState('')
  const [autocompleteOptions, setAutocompleteOptions] = useState([])

  const downinfo = (
    <div>
      <a href={demoxlsadditivematerials} download>
        <Button type="dashed">{t('my_materials.example_download')}</Button>
      </a>
    </div>
  )

  const queryClient = useQueryClient()

  const readExcel = async (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.readAsArrayBuffer(file)

      fileReader.onload = (e) => {
        const bufferArray = e.target.result
        const wb = XLSX.read(bufferArray)
        const wsname = wb.SheetNames[0]
        let ws = wb.Sheets[wsname]

        const data = XLSX.utils.sheet_to_json(ws)

        const formattedData = data.map((row) => ({
          material_name:
            row['“Additive Material” / “Additives Material”'] !== undefined &&
            row['“Additive Material” / “Additives Material”'] !== null
              ? String(row['“Additive Material” / “Additives Material”']).trim()
              : '',
        }))

        resolve(formattedData)
      }

      fileReader.onerror = (error) => {
        reject(error)
      }
    })
  }

  const { data: operatorAdditiveMaterialsData } = useQuery(
    ['operatorAdditiveMaterialsForCheck'],
    async () => {
      let allMaterials = []
      let currentPage = 1
      let hasMorePages = true
      let totalCount = 0

      while (hasMorePages) {
        const pageData = await fetchAllOperatorAdditiveMaterials({
          queryKey: [
            'operatorAdditiveMaterialsForCheck',
            { page: currentPage, size: 100 },
          ],
        })

        if (pageData.items && pageData.items.length > 0) {
          allMaterials = [...allMaterials, ...pageData.items]
          totalCount = pageData.total

          const totalFetched = currentPage * 100
          hasMorePages = totalFetched < pageData.total
          currentPage += 1
        } else {
          hasMorePages = false
        }
      }

      return {
        items: allMaterials,
        size: allMaterials.length,
        total: totalCount,
      }
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  )

  const addMaterialsViaExcelMutation = useMutation(
    createOperatorAdditiveMaterials,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ['operatorAdditiveMaterialsForMapperPage'],
        })
        queryClient.refetchQueries({
          queryKey: ['operatorAdditiveMaterialsForMapperPage'],
        })
        setLoading(false)
        notification.success({
          message: t('my_materials.materials_added', {
            number: data.created_materials.length,
          }),
        })
        data.created_materials.length < items.length &&
          notification.info({
            message: t('my_materials.ignored_materials_message'),
          })
        data.skipped_invalid_materials.length > 0 &&
          notification.warning({
            message: t('my_materials.invalid_materials_skipped', {
              names: data.skipped_invalid_materials.join(', '),
            }),
          })
        queryClient.invalidateQueries({
          queryKey: ['operatorAdditiveMaterialsKpiCount'],
        })
      },
      onError: (_error) => {
        setLoading(false)
        notification.error({
          message: t('error_adding_material'),
        })
      },
    },
  )

  const addMaterialMutation = useMutation(createOperatorAdditiveMaterial, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['operatorAdditiveMaterialsForMapperPage'],
      })
      queryClient.invalidateQueries({
        queryKey: ['operatorAdditiveMaterialsKpiCount'],
      })
      setIsAddModalVisible(false)
      setNewMaterialName('')
      setAutocompleteOptions([])
      notification.success({
        message: t('material_added_successfully', { name: data.material_name }),
      })
    },
    onError: (_error) => {
      notification.error({
        message: t('error_adding_material'),
      })
      setIsAddModalVisible(false)
      setNewMaterialName('')
      setAutocompleteOptions([])
    },
  })

  const handleAutocompleteSearch = async (searchText) => {
    if (searchText && searchText.length >= 2) {
      try {
        const results =
          await fetchAutocompleteOperatorAdditiveMaterials(searchText)
        setAutocompleteOptions(results.map((name) => ({ value: name })))
      } catch (_error) {
        antdNotification.error({ message: t('autocomplete_failed_message') })
        setAutocompleteOptions([])
      }
    } else {
      setAutocompleteOptions([])
    }
  }

  const onAutocompleteSelect = (value) => {
    setNewMaterialName(value)
    const normalizedSelectedName = normalizeName(value)
    const existingMaterial = operatorAdditiveMaterialsData?.items?.find(
      (item) => normalizeName(item.material_name) === normalizedSelectedName,
    )
    if (existingMaterial) {
      notification.info({
        message: t('material_selected_exists', { name: value }),
      })
      setHighlightedMaterialId(existingMaterial.id)
      setListActionTrigger((prev) => prev + 1)
      setIsAddModalVisible(false)
      setNewMaterialName('')
      setAutocompleteOptions([])
    }
  }

  const handleUpload = async (file) => {
    const items = await readExcel(file)
    setIsModalOpen(true)
    setItems(items)
  }

  const handleOkUpload = () => {
    setIsModalOpen(false)
    setLoading(true)
    setFileList([])

    addMaterialsViaExcelMutation.mutate(items)
  }

  const handleCancelUpload = () => {
    setIsModalOpen(false)
    setFileList([])
  }

  const handleOpenAddModal = () => {
    queryClient.invalidateQueries['operatorAdditiveMaterialsForMapperPage']
    setIsAddModalVisible(true)
  }

  const handleModalOk = () => {
    if (!newMaterialName.trim()) {
      notification.error({ message: t('material_name_required') })
      return
    }
    addMaterialMutation.mutate({ material_name: newMaterialName.trim() })
  }

  const handleModalCancel = () => {
    setIsAddModalVisible(false)
    setNewMaterialName('')
    setAutocompleteOptions([])
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <span>{t('operator_additive_materials.header')}</span>
      <Flex justify="flex-end" vertical id="importMaterialsContainer">
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {loading ? (
            <div
              style={{
                textAlign: 'center',
                marginLeft: 'auto',
                marginRight: 'auto',
                verticalAlign: 'middle',
                padding: '25% 0',
              }}
            >
              <Spinner />
              {t('my_materials.upload_in_progress')}
            </div>
          ) : (
            <Upload
              name="file"
              accept=".xlsx"
              maxCount={1}
              fileList={fileList}
              onChange={({ fileList: newFileList }) => setFileList(newFileList)}
              beforeUpload={(info) => {
                const isXlsx =
                  info.type ===
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                  info.name.endsWith('.xlsx') // fallback check

                if (!isXlsx) {
                  notification.error({
                    message: t('my_materials.file_type_error'),
                  })
                  return Upload.LIST_IGNORE
                }
                handleUpload(info)

                // always return false to disable default upload as we are handling that differently
                return false
              }}
              itemRender={(originNode, _file) => {
                return (
                  <div
                    style={{
                      maxWidth: 80,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {originNode}
                  </div>
                )
              }}
            >
              <Tooltip
                placement="bottom"
                title={t('my_materials.import_list_tooltip')}
              >
                <Button
                  icon={<UploadOutlined />}
                  type="primary"
                  size="medium"
                  style={{ borderRadius: '10px 0 0 10px' }}
                >
                  {t('my_materials.import_button')}
                </Button>
              </Tooltip>
            </Upload>
          )}
          <Button
            icon={<PlusOutlined />}
            onClick={handleOpenAddModal}
            size="medium"
            style={{
              borderRadius: '0 10px 10px 0',
              borderColor: token.colorPrimary,
              color: token.colorPrimary,
            }}
          >
            {t('add_material_button')}
          </Button>
        </div>
        <div id="xlsxinfo">
          <Space>
            {t('my_materials.excel_file')}
            <Popover
              placement="bottomRight"
              content={downinfo}
              title={t('Download')}
            >
              <a href={demoxlsadditivematerials} download>
                <DownloadOutlined />
              </a>
            </Popover>
          </Space>
        </div>
      </Flex>

      <Modal
        title={t(
          'operator_additive_materials.add_material_title',
          'Add New Operator Additive Material',
        )}
        open={isAddModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={t('add_button')}
        cancelText={t('cancel_button')}
      >
        <AutoComplete
          options={autocompleteOptions}
          style={{ width: '100%' }}
          onSelect={onAutocompleteSelect}
          onSearch={handleAutocompleteSearch}
          placeholder={t('enter_material_name_placeholder')}
          value={newMaterialName}
          onChange={(value) => setNewMaterialName(value)}
          filterOption={false}
          showSearch={true}
          allowClear={true}
        />
      </Modal>

      <Modal
        destroyOnClose="true"
        centered="true"
        title={t('my_materials.upload_confirmation_modal')}
        open={isModalOpen}
        onOk={handleOkUpload}
        onCancel={handleCancelUpload}
        maskClosable={false}
        okText={t('add_button')}
        cancelText={t('cancel_button')}
      />
    </div>
  )
}

export default AddOperatorAdditiveMaterial
